import { CSSProperties, RefObject } from 'react';
import { Assert } from '@team/assert';
import { clsx } from '@team/clsx';
import { Mode, State } from './enums';
import { Dimension } from './types';
import { INITIAL_DIMENSION } from './const';

export const isOriginalState = (state: State) => state === State.ORIGINAL;

export const isFixedState = (state: State) => state === State.FIXED;

export const isReleasedState = (state: State) => state === State.RELEASED;

export const buildOuterStyle = (state: State, stickyDimenstion: Dimension) => {
  const output: CSSProperties = {};

  if (isOriginalState(state)) {
    return output;
  }

  output.height = stickyDimenstion.height;

  return output;
};

export const buildInnerStyle = (
  mode: Mode,
  state: State,
  stickyDimenstion: Dimension,
  offset: number,
  externalStyle?: CSSProperties,
  externalFixedStyle?: CSSProperties,
  externalReleasedStyle?: CSSProperties
) => {
  const output: CSSProperties = {
    ...externalStyle,
    position: 'relative',
  };

  if (isOriginalState(state)) {
    return output;
  }

  output.width = stickyDimenstion.width;

  if (isReleasedState(state)) {
    Object.assign(output, externalReleasedStyle);

    return output;
  }

  Object.assign(output, externalFixedStyle);

  output.position = 'fixed';
  output.zIndex = 'var(--tgui--z-index-overlay)';

  if (mode === Mode.TOP) {
    output.top = 0;
    output.transform = `translate3d(0, ${offset}px, 0)`;

    return output;
  }

  output.bottom = 0;
  output.transform = `translate3d(0, -${offset}px, 0)`;

  return output;
};

export const buildInnerClassName = (
  state: State,
  externalClassName?: string,
  externalFixedClassName?: string,
  externalReleasedClassName?: string
) => {
  const list: [Maybe<string>] = [externalClassName];

  if (isOriginalState(state)) {
    return clsx(list);
  }

  if (isFixedState(state)) {
    list.push(externalFixedClassName);

    return clsx(list);
  }

  list.push(externalReleasedClassName);

  return clsx(list);
};

export const isShouldSticky = (
  mode: Mode,
  stickyDimenstion: Dimension,
  scrollDimention: Dimension,
  offset: number
) => {
  if (mode === Mode.TOP) {
    const shouldSticky = stickyDimenstion.y < scrollDimention.top + offset;

    return shouldSticky;
  }

  const shouldSticky =
    stickyDimenstion.y + stickyDimenstion.height >
    scrollDimention.height + scrollDimention.top - offset;

  return shouldSticky;
};

export const getStateByStickyFlag = (shouldSticky: boolean) =>
  shouldSticky ? State.FIXED : State.RELEASED;

export const getScrollTarget = (
  containerRef?: RefObject<HTMLElement>
): HTMLElement | Window => {
  if (Assert.isUndefined(containerRef) || Assert.isNull(containerRef.current)) {
    return window;
  }

  return containerRef.current;
};

export const getDimention = (target: HTMLElement | Window): Dimension => {
  if (target === window) {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      x: window.scrollX,
      y: window.screenY,
      top: 0,
      bottom: 0,
    };
  }

  if (Assert.isNodeInstance(target)) {
    return target.getBoundingClientRect();
  }

  return INITIAL_DIMENSION;
};
